import React, { Component } from 'react';
import Tone from 'tone';
import { Text, Circle, Group, Stage, Layer, Rect, Line} from 'react-konva';

const ATTACK = 'ATTACK';
const DECAY = 'DECAY';
const SUSTAIN = 'SUSTAIN';
const RELEASE = 'RELEASE';

const DEFAULT_SUSTAIN = 100;
const LINE_COLOR = "#6200EE";
const HEIGHT=400;
const WIDTH=650;
const GUIDE_LINE_SUBDIVISIONS = 20; // divide the screen into 20 subdivisions

/**
 * Modal responsible for sequence-wide changes like
 * double setting the track length
 */
class EnvelopeDrawer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            updateRate: 100,
            x:0,
            y:0
        };

    }

    componentDidMount() {
        this.run();
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    run() {
        // continuously update the canvas so that LFO
        // modulates of the envelope can be visualized
        this.timer = setInterval(() => {
            this.setState(this.state);
        }, this.state.updateRate);
    }


    startDragging(pt) {
        this.setState({dragging: pt});
        this.props.setSelectedPoint(pt);
    }

    stopDragging(pt) {
        this.setState({dragging: undefined});
    }

    onMouseMove(e) {
        let pointer = this.ref.getPointerPosition();
        let x = pointer.x;
        let y = pointer.y;
        this.setState({x: x, y: y});

        if (this.state.dragging === undefined) {
            return;
        }

        let time = this.getTimeForXPosition(x, this.state.dragging);
        if (this.state.dragging === ATTACK) {
            this.props.envelope.attack = time;
            this.props.envelope.level = this.getLevelForYPosition(y);
        } else if (this.state.dragging === DECAY) {
            this.props.envelope.decay = time;
            this.props.envelope.sustain = this.getSustainForYPosition(y);
        } else if (this.state.dragging === SUSTAIN) {
            this.props.envelope.sustain = this.getSustainForYPosition(y);
        } else if (this.state.dragging === RELEASE) {
            this.props.envelope.release = time;
        }

        this.props.onEnvelopeUpdated();
    }

    getGuideLines() {
        let lines = [];
        let width = WIDTH / GUIDE_LINE_SUBDIVISIONS;
        let x=0;
        for (let i=0; i <= GUIDE_LINE_SUBDIVISIONS; i++) {
            lines.push(
                <Line
                  points={[x, 0, x, HEIGHT]} 
                  stroke='#1f1f1f'
                  strokeWidth={1}/>
            );
            x+=width;
        }

        lines.push(
            <Line
              points={[0, HEIGHT/4, WIDTH, HEIGHT/4]}
              stroke='#1f1f1f'
              strokeWidth={1}/>);
        lines.push(
            <Line
              points={[0, 3*HEIGHT/4, WIDTH, 3*HEIGHT/4]}
              stroke='#1f1f1f'
              strokeWidth={1}/>);
        x = width;
        let time=this.props.zoom;
        for (let i=0; i < GUIDE_LINE_SUBDIVISIONS; i+=2) {
            lines.push(
                <Text
                  text={time + ' ms'}
                  fill='white'
                  fontSize={6}
                  fontFamily='Arial'
                  x={x + 2}
                  y={HEIGHT - 10}
                  />);

            x += width * 2;
            time += this.props.zoom * 2;
        }
        return lines;
    }

    getAttackCurve() {
        let x = this.getXPositionForTime(this.props.envelope.attack);
        let y = this.getYForLevel();
        if (this.attackCurve === 'linear') {
            return (
                <Line
                  points={[0, HEIGHT/2, x, y]}
                  strokeWidth={2}
                  stroke={LINE_COLOR}/>
            );
        } else if (this.attackCurve === 'exponential') {
            return this.getExponentialCurve(
                0,
                HEIGHT/2,
                x,
                y,
                true);
        } else {
            let curve = Tone.Envelope.Type[this.attackCurve];
            return this.getExplicitCurve(curve.In, 0, HEIGHT/2, x, y, false);
        }
    }

    getExplicitCurve(curve, x1, y1, x2, y2, isSustain) {
        let width = x2 - x1;
        let partWidth = width / curve.length;
        let pts = [];
        for (let i=0; i < curve.length; i++) {
            let y = isSustain ?
                this.getYForSustain(curve[i]*this.props.envelope.sustain) :
                this.getYForLevel(curve[i]*this.level);
            pts.push(x1);
            pts.push(y);
            x1 += partWidth;
        }
        return (
            <Line
              points={pts}
              strokeWidth={2}
              stroke={LINE_COLOR}/>
        );
    }

    getDecayCurve() {
        if (this.decayCurve === 'linear') {
            return (
                <Line
                  points={[this.attackX, this.getYForLevel(), this.decayX, this.sustainY]}
                  strokeWidth={2}
                  stroke={LINE_COLOR}/>);
        } else if (this.decayCurve === 'exponential') {
            return this.getExponentialCurve(
                this.attackX,
                this.getYForLevel(),
                this.decayX,
                this.sustainY);
        } else {
            let curve = Tone.Envelope.Type[this.decayCurve];
            return this.getExplicitCurve(
                curve.Out,
                this.attackX,
                this.getYForLevel(),
                this.decayX,
                this.sustainY);
        }
    }

    getSustainCurve() {
        return (
            <Line
              points={[this.decayX, this.sustainY, this.sustainX, this.sustainY]}
              strokeWidth={2}
              stroke={LINE_COLOR}/>
        );
    }

    getReleaseCurve() {
        if (this.releaseCurve === 'linear') {
            return (
                <Line
                  points={[this.sustainX, this.sustainY, this.releaseX, HEIGHT/2]}
                  strokeWidth={2}
                  stroke={LINE_COLOR}/>
            );
        } else if (this.releaseCurve === 'exponential') {
            return this.getExponentialCurve(
                this.sustainX,
                this.sustainY,
                this.releaseX,
                HEIGHT/2);
        } else {
            let curve = Tone.Envelope.Type[this.releaseCurve];
            return this.getExplicitCurve(
                curve.Out,
                this.sustainX,
                this.sustainY,
                this.releaseX,
                HEIGHT/2,
                true);
        }
    }

    getExponentialCurve(x1, y1, x2, y2, isIn=false) {
        let midPointX = (2*x1 + x2) / 3;
        if (isIn) {
            midPointX = (4*x2 + x1) / 5;
        }
        let midPointY = ((((y1 + y2) / 2)) + y2) / 2;
        if (isIn) {
            midPointY = (y1 + y2) / 2;
        }
        return (
            <Line
              points={[x1, y1, midPointX, midPointY, x2,y2]}
              tension={0.3}
              strokeWidth={2}
              stroke={LINE_COLOR}/>
        );
    }

    getAttackPoint() {
        let color = this.props.selectedPoint === ATTACK ?
            'yellow' : 'black';
        return (
            <Circle
              onMouseDown={(e) => this.startDragging(ATTACK)}
              onMouseUp={(e) => this.stopDragging(ATTACK)}
              x={this.attackX}
              y={this.getYForLevel()}
              radius={5}
              stroke='yellow'
              fill={color}/>
        );
    }

    getDecayPoint() {
        let color = this.props.selectedPoint === DECAY ?
            'yellow' : 'black';
        return (
            <Circle
              onMouseDown={(e) => this.startDragging(DECAY)}
              onMouseUp={(e) => this.stopDragging(DECAY)}
              x={this.decayX}
              y={this.sustainY}
              radius={5}
              stroke='yellow'
              fill={color}/>
        );
    }

    getSustainPoint() {
        let color = this.props.selectedPoint === SUSTAIN ?
            'yellow' : 'black';
        return (
            <Circle
              onMouseDown={(e) => this.startDragging(SUSTAIN)}
              onMouseUp={(e) => this.stopDragging(SUSTAIN)}
              x={this.sustainX}
              y={this.sustainY}
              radius={5}
              stroke='yellow'
              fill={color}/>
        );
    }

    getReleasePoint() {
        let color = this.props.selectedPoint === RELEASE ?
            'yellow' : 'black';
        return (
            <Circle
              onMouseDown={(e) => this.startDragging(RELEASE)}
              onMouseUp={(e) => this.stopDragging(RELEASE)}
              x={this.releaseX}
              y={HEIGHT / 2}
              radius={5}
              stroke='yellow'
              fill={color}/>
        );
    }

    getADSRPoints() {
        return [
            this.getAttackPoint(),
            this.getDecayPoint(),
            this.getSustainPoint(),
            this.getReleasePoint()];
    }

    render() {
        return (
            <div>
              <Stage
                ref={(ref) => this.ref = ref}
                width={WIDTH} height={HEIGHT}
                onMouseLeave={() => this.stopDragging()}
                onMouseUp={(e) => this.stopDragging()}>
                <Layer>
                  <Rect
                    onMouseMove={(e) => this.onMouseMove(e)}
                    onMouseUp={(e) => this.stopDragging()}
                    x={0} y={0} height={HEIGHT} width={WIDTH} fill="black"/>
                  {this.getGuideLines()}
                  <Line points={[0, HEIGHT/2, WIDTH, HEIGHT/2]} strokeWidth={2} stroke="red"/>
                  {this.getAttackCurve()}
                  {this.getDecayCurve()}
                  {this.getSustainCurve()}
                  {this.getReleaseCurve()}
                  {this.getADSRPoints()}
                <Text
                  text={this.state.x + ',' + this.state.y}
                  fill='white'
                  fontSize={6}
                  fontFamily='Arial'
                  x={30}
                  y={300}
                  />
                </Layer>
              </Stage>
            </div>
        );
    }

    get level() {
        return this.props.showLevel ? this.props.envelope.level : 1;
    }

    getYForSustain(sustain) {
        sustain = sustain * this.level;
        let y = sustain * HEIGHT / 2;
        return HEIGHT/2 - y;
    }

    get sustainY() {
        let sustain = this.props.envelope.sustain * this.level;
        let y = sustain * HEIGHT / 2;
        return HEIGHT/2 - y;
    }

    getYForLevel(level = this.level) {
        return HEIGHT / 2 - level * HEIGHT / 2;
    }

    get attackX() {
        return this.getXPositionForTime(this.props.envelope.attack);
    }

    get decayX() {
        return this.getXPositionForTime(this.props.envelope.decay) + this.attackX;
    }

    get sustainX() {
        return this.attackX + this.decayX + DEFAULT_SUSTAIN;
    }

    get releaseX() {
        return this.sustainX + this.getXPositionForTime(this.props.envelope.release);
    }

    getLevelForYPosition(y) {
        if (!this.props.showLevel) {
            return 1;
        }
        let level = -1 + 2 * (HEIGHT - y) / HEIGHT;
        if (level > 1) {
            return 1;
        } else if (level < -1) {
            return 0;
        }
        return level;
    }

    getSustainForYPosition(y) {
        let sustain = -1 + 2 * (HEIGHT - y) / HEIGHT;
        if (!this.props.showLevel) {
            sustain = Math.abs(sustain);
        }
        sustain = sustain / this.level;
        if (sustain > 1) {
            return 1;
        } else if (sustain < 0) {
            return 0;
        }
        return sustain;
    }

    getTimeForXPosition(x, curve) {
        let width = WIDTH / GUIDE_LINE_SUBDIVISIONS;
        if (curve === DECAY) {
            x -= this.attackX;
        } else if (curve === SUSTAIN) {
            x -= this.decayX;
        } else if (curve === RELEASE) {
            x -= this.sustainX;
        }
        return Math.max(0, x / width * this.props.zoom);
    }

    // returns x position for time in ms 
    getXPositionForTime(time) {
        let width = WIDTH / GUIDE_LINE_SUBDIVISIONS;
        return width * time / this.props.zoom;
    }

    get isSynthEnvelope() {
        return this.props.envelopeType !== 'PITCH';
    }

    get attackCurve() {
        return this.props.envelope.attackCurve;
    }

    get decayCurve() {
        return this.props.envelope.decayCurve;
    }

    get sustainCurve() {
        return this.props.envelope.sustainCurve;
    }

    get releaseCurve() {
        return this.props.envelope.releaseCurve;
    }
}

export default EnvelopeDrawer;
